import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Icon, Badge } from '@folio/stripes/components';
import { getTranslatedErrorMessage } from './BackendErrorTranslations';

/**
 * ErrorDisplay Component
 *
 * Displays individual error items in a formatted way
 */
const ErrorDisplay = ({ errors }) => {
  const intl = useIntl();

  if (!errors || errors.length === 0) {
    return null;
  }

  const getTranslatedOperation = (operation) => {
    if (!operation) return '';

    // Convert operation to string if it's an object
    let operationStr =
      typeof operation === 'object'
        ? operation.props?.defaultMessage || operation.toString()
        : operation;

    // Convert operation text to translation key
    const operationKeyMap = {
      'Create instance': 'createInstance',
      'Create holding': 'createHolding',
      'Create item': 'createItem',
      'Update instance': 'updateInstance',
      'Update holding': 'updateHolding',
      'Update item': 'updateItem',
      'Delete instance': 'deleteInstance',
      'Delete holding': 'deleteHolding',
      'Delete item': 'deleteItem',
      Unknown: 'unknown',
    };

    // Extract base operation (remove parameters like location, barcode)
    const baseOperation = operationStr.split(' ').slice(0, 2).join(' ');
    const operationKey = operationKeyMap[baseOperation] || operationStr;

    try {
      return intl.formatMessage(
        {
          id: `ui-plugin-inventory-templates.createProgress.operation.${operationKey}`,
        },
        { defaultMessage: operationStr },
      );
    } catch (e) {
      return operationStr;
    }
  };

  return (
    <div
      style={{
        maxHeight: '60vh',
        overflowY: 'auto',
        backgroundColor: '#f8f9fa',
        padding: '16px',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
      }}
    >
      {errors.map((error, index) => (
        <div
          key={index}
          style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#ffffff',
            border: '1px solid #f5c6cb',
            borderLeft: '4px solid #dc3545',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <Icon
              icon="times-circle"
              size="medium"
              style={{ color: '#dc3545', marginRight: '8px' }}
            />
            <Badge
              type={
                error.category === 'instance'
                  ? 'danger'
                  : error.category === 'holdings'
                    ? 'warning'
                    : 'default'
              }
            >
              <FormattedMessage
                id={`ui-plugin-inventory-templates.errorDetails.category.${error.category}`}
                defaultMessage={error.category}
              />
            </Badge>
            <span
              style={{
                marginLeft: '8px',
                fontSize: '0.85rem',
                color: '#6c757d',
              }}
            >
              {getTranslatedOperation(error.operation)}
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '0.8rem',
                color: '#6c757d',
              }}
            >
              {new Date(error.timestamp).toLocaleString()}
            </span>
          </div>
          <div
            style={{
              color: '#721c24',
              fontWeight: '500',
              marginBottom: '4px',
            }}
          >
            {getTranslatedErrorMessage(error, intl)}
          </div>
          {error.code !== 'UNKNOWN' && (
            <div
              style={{
                fontSize: '0.8rem',
                color: '#495057',
                fontFamily: 'monospace',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.errorDetails.code"
                defaultMessage="Error Code: {code}"
                values={{ code: error.code }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

ErrorDisplay.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
      displayMessage: PropTypes.string,
      category: PropTypes.string,
      operation: PropTypes.string,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

ErrorDisplay.defaultProps = {
  errors: [],
};

export default ErrorDisplay;
